/**
 * PrecommitExplainer — progressive-disclosure explainer for PRECOMMIT (W).
 *
 * PRECOMMIT neutralizes the vulnerability class "irreversible effect run
 * without a precommitment witness" by indexing every computation with a
 * grade drawn from an effect monoid. An irreversible grade is inhabited
 * only when the precommitment witness is embedded in the type constructor,
 * so the unwitnessed irreversible action is structurally inexpressible.
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

import React from "react";
import { PRECOMMIT_META } from "@csk/core";
import { THEOREM_COLOR, withAlpha } from "../../design-system/tokens.js";
import { MathInline, tex } from "../../components/math/index.js";
import {
  ExplainerCard,
  GradeLadderDiagram,
  type ExplainerCrossLink,
  type ExplainerIdentity,
  type ExplainerTierContent,
  type GradeLadderRung,
} from "../shared/index.js";
import { TheoremMetaStrip } from "./TheoremMetaStrip.js";
import { TIER_ONE_IMMUNITY_LINK } from "./tierOneCrossLinks.js";

const ACCENT = THEOREM_COLOR.PRECOMMIT;

const LADDER_RUNGS: readonly GradeLadderRung[] = [
  { grade: "pure", note: "no effect — composes freely" },
  { grade: "reversible", note: "effect with a compensating inverse" },
  { grade: "irreversible", note: "requires an embedded precommitment witness" },
];

const IDENTITY: ExplainerIdentity = {
  id: "explainer-precommit",
  kind: "SSM · Neutralization Theorem (S1)",
  title: "PRECOMMIT",
  subtitle: "W · Graded monad · IMMUNE · G3 Time",
  accent: ACCENT,
};

const CROSS_LINKS: readonly ExplainerCrossLink[] = [
  { label: "Graded monad (structure)", href: "#explainer-graded-monad", accent: ACCENT },
  {
    label: "INV.ESTABLISH (G3 sibling)",
    href: "#explainer-inv-establish",
    accent: THEOREM_COLOR["INV.ESTABLISH"],
  },
  {
    label: "INV.PRESERVE (G3 sibling)",
    href: "#explainer-inv-preserve",
    accent: THEOREM_COLOR["INV.PRESERVE"],
  },
  TIER_ONE_IMMUNITY_LINK,
];

const TIERS: ExplainerTierContent = {
  tldr: (
    <p>
      <strong>PRECOMMIT</strong> says: an irreversible action carries its precommitment with it, by
      construction. You cannot write an irreversible step that has not first committed.
    </p>
  ),
  intuition: (
    <>
      <p>
        Some actions cannot be undone: charging a card, deleting a backup, signing a release. The
        usual safeguard is a confirmation dialog or a runtime check — a story about asking before
        the irreversible thing happens, which fails the moment the check is skipped or races a
        second request.
      </p>
      <p>
        CSK grades every computation with an effect level drawn from a monoid. The{" "}
        <em>irreversible</em> grade is inhabited only when a precommitment witness is part of the
        type constructor. A computation that climbs to the irreversible rung without that witness is
        unconstructable in the source.
      </p>
    </>
  ),
  formal: (
    <>
      <p>
        Let <MathInline>{tex`(M, \cdot, e)`}</MathInline> be the effect-grade monoid and{" "}
        <MathInline>{tex`T : M \to (C \to C)`}</MathInline> the graded monad. A computation has type{" "}
        <MathInline>{tex`T^{g} A`}</MathInline> for a grade <MathInline>{tex`g \in M`}</MathInline>;
        Kleisli composition multiplies grades:{" "}
        <MathInline>{tex`T^{g} A \to (A \to T^{h} B) \to T^{g \cdot h} B`}</MathInline>.
      </p>
      <p style={{ marginTop: "0.5rem" }}>
        The grade <em>irreversible</em> has no constructor that does not carry the precommitment
        witness. Composition cannot launder a missing witness, because the product grade is at least
        as committed as each factor. <code>PRECOMMIT</code> is a projection of the Categorical
        Immunity Theorem and pairs with the <code>REQUIRE</code> SDP (monad{" "}
        <MathInline>{tex`\dashv`}</MathInline> comonad).
      </p>
    </>
  ),
  relevance: (
    <>
      <p>
        <strong>Vulnerability class neutralized:</strong> phase / temporal-discipline violation — an
        irreversible effect taken before its commitment, including request-forgery and
        unchecked-state-change patterns (CWE-352, CWE-754, CWE-841). Realized by{" "}
        <em>structural inexpressibility</em>: the unwitnessed irreversible grade is uninhabited.
      </p>
      <p>
        <strong>Theorem-collision check.</strong> If you reach for a raw side effect outside the
        graded monad, you have left the kernel category. PRECOMMIT is then vacuous on that effect —
        the grade requirement has gone away, not been bypassed.
      </p>
      <p>
        <strong>Seam type</strong> (per <code>theory/structural/SEAM-THEORY.md</code>):{" "}
        {PRECOMMIT_META.seamType}. <strong>Bridge</strong>: precommitment evidence (governance
        artifact).
      </p>
    </>
  ),
  references: [
    {
      label: "PRECOMMIT theorem card",
      path: "docs/reference/theorem-cards/PRECOMMIT.md",
    },
    {
      label: "Kernel module (main PRECOMMIT neutralization theorem)",
      path: `agda/${PRECOMMIT_META.agdaSource}`,
      note: `PRECOMMIT_META.agdaSource is "${PRECOMMIT_META.agdaSource}"; the repo path is prefixed with "agda/". Main theorem in module lines 74–199.`,
    },
    {
      label: "Graded-monad MathLibCore module",
      path: "agda/MathLibCore/CategoryTheory/GradedMonad.agda",
    },
    {
      label: "Theorem Canon (§4.1 IMMUNE; §7 REQUIRE SDP)",
      path: "docs/reference/Theorem-Canon.md",
    },
  ],
};

const DIAGRAM = (
  <>
    <TheoremMetaStrip
      theoremName="PRECOMMIT"
      code="W"
      structure="Graded monad"
      mode="immune"
      generator="G3"
      seamType={PRECOMMIT_META.seamType}
      bridgeArtifact="precommitment evidence"
      theoremColor={ACCENT}
      washColor={withAlpha(ACCENT, "1A")}
    />
    <div style={{ marginTop: "0.5rem" }}>
      <GradeLadderDiagram
        rungs={LADDER_RUNGS}
        accent={ACCENT}
        ariaLabel="Effect-grade ladder: pure climbs to reversible climbs to irreversible; the irreversible rung requires an embedded precommitment witness"
      />
    </div>
  </>
);

export function PrecommitExplainer(): React.JSX.Element {
  return (
    <ExplainerCard identity={IDENTITY} tiers={TIERS} diagram={DIAGRAM} crossLinks={CROSS_LINKS} />
  );
}
